import { Flex, Group, Select, TextInput } from "@mantine/core";
import {
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
} from "mantine-react-table";
import { IconChevronDown } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  type BasicEntity,
  type BasicResponse,
  type TableToolbarProps,
} from "./types";
import viewClasses from "./modules/TableMantine.module.css";
import titleClasses from "./modules/TableMantineTitle.module.css";
import searchClasses from "./modules/TableMantineSearch.module.css";
import ExportButton from "./ExportButton/ExportButton";
import { api } from "@/lib/api";
import { useUserContext } from "../Layout/Contexts/User/useUserContext";

export function TableToolbar<
  TData extends BasicEntity,
  TResponse extends BasicResponse<TData> = BasicResponse<TData>,
>({
  viewOptions,
  activeView,
  onViewChange,
  title,
  searchTerm,
  resourcePath,
  onSearchChange,
  searchDisabled = false,
  table,
  customToolbarContent,
  onExportLoadingChange,
}: TableToolbarProps) {
  const { t } = useTranslation("features"); // Assuming 'features' namespace
  const { roles } = useUserContext();
  const isAdmin = roles.includes("Admin");
  const fetchAllData = async (): Promise<TData[]> => {
    try {
      // Build URL with pagination disabled to get all data
      const url = `${resourcePath}`;
      console.log("TableToolbar fetchAllData: Making API call to:", url);

      const response = await api.get<TResponse>(url, {
        params: {
          singlePage: true,
        },
      });

      const dataRaw = response.data;
      const data = dataRaw?.data || [];

      // Return the items from the paged response
      return data;
    } catch (error) {
      console.error(
        "TableToolbar fetchAllData: Error fetching all data:",
        error,
      );
      throw error;
    }
  };

  return (
    <Group w={"100%"} justify="space-between" grow m={4}>
      <Flex
        justify="flex-start"
        align="center"
        direction="row"
        wrap="nowrap"
        gap={"xs"}
      >
        {viewOptions && viewOptions.length > 0 ? (
          <Select
            rightSection={<IconChevronDown size={16} />}
            leftSection={<></>}
            leftSectionPointerEvents="none"
            classNames={viewClasses}
            data={viewOptions}
            value={activeView?.value} // Controlled component
            allowDeselect={false}
            onChange={onViewChange} // Directly pass the handler
            disabled={!viewOptions || viewOptions.length === 0} // Disable if no options
          />
        ) : (
          <Select // Display Title if no viewOptions
            rightSection={<></>}
            leftSection={<></>}
            classNames={titleClasses}
            data={[{ value: title ?? "", label: title ?? "" }]}
            defaultValue={title}
            readOnly
            allowDeselect={false}
          />
        )}
        {!searchDisabled && (
          <TextInput
            size="xs"
            placeholder={t("common.searchPlaceholder")}
            value={searchTerm}
            classNames={searchClasses}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            style={{ width: "276px" }}
          />
        )}
      </Flex>
      <Flex
        gap={"xs"}
        justify="right"
        align="center"
        direction="row"
        wrap="wrap"
      >
        {customToolbarContent}
        <MRT_ShowHideColumnsButton table={table} />
        {isAdmin && (
          <ExportButton
            table={table}
            viewName={activeView?.label}
            tableName={title}
            fetchAllData={fetchAllData}
            onLoadingChange={onExportLoadingChange}
          />
        )}

        <MRT_ToggleFullScreenButton table={table} mr={20} />
      </Flex>
    </Group>
  );
}
